import clsx from "clsx";
import {
  ChangeEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { FieldError } from "../FieldError";
import { useIsMobile } from "../hooks";
import { Typo } from "../Typo";

export type TextAreaV2Props = {
  name?: string;
  error?: string;
  label?: string;
  labelHint?: string;
  description?: string;
  placeholder?: string;
  maxLength?: number;
  hideCounter?: boolean;
  minHeight?: number;
  onChange?: (value: string) => void;
  value?: string;
};

export const TextAreaV2 = ({
  name,
  error,
  label,
  labelHint,
  description,
  placeholder,
  maxLength,
  hideCounter,
  minHeight = 64,
  onChange,
  value,
}: TextAreaV2Props) => {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const [count, setCount] = useState(value?.length || 0);
  const textareaId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const HEADER_OFFSET = 80; // Offset for fixed header height

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const currentValue = e.target.value;
    setCount(currentValue.length);
    onChange?.(e.target.value);
  };

  const scrollToTop = () => {
    if (isMobile && containerRef.current) {
      const element = containerRef.current;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - HEADER_OFFSET;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = "auto";
      const newHeight = Math.max(minHeight, textarea.scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value, minHeight]);

  const hasError = error || (maxLength && count > maxLength);
  const isOverLimit = maxLength ? count > maxLength : false;

  const textareaClasses = clsx(
    "min-h-[64px]",
    "p-2",
    "rounded-lg",
    "font-body",
    "text-[0.875rem]",
    "bg-surface-tonal-default",
    "antialiased",
    "text-text-primary",
    "border-2",
    "focus:outline-none",
    "overflow-y-hidden",
    "resize-none",
    hasError
      ? "border-stroke-error focus:border-stroke-error"
      : "border-transparent focus:border-stroke-focused",
  );

  const counterText = useMemo(() => {
    if (!maxLength) return null;

    const numberFormatter = new Intl.NumberFormat(i18n.language);
    const formattedCount = numberFormatter.format(count);
    const formattedMaxLength = numberFormatter.format(maxLength);

    return `${formattedCount}/${formattedMaxLength} ${t(
      "questionnairePage_characters_label",
    )}`;
  }, [maxLength, count, t, i18n.language]);

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {label && (
        <label htmlFor={textareaId} className="flex flex-col gap-1">
          <span className="inline-flex items-baseline gap-1">
            <Typo
              type={isMobile ? "labelSM" : "labelMD"}
              textColor="text-text-base"
            >
              {label}
            </Typo>
            {labelHint && (
              <Typo type="bodySM" textColor="text-text-secondary">
                {labelHint}
              </Typo>
            )}
          </span>

          {description && (
            <Typo element="p" type="bodyXS" textColor="text-text-secondary">
              {description}
            </Typo>
          )}
        </label>
      )}

      <textarea
        ref={textareaRef}
        id={textareaId}
        className={textareaClasses}
        style={{ minHeight }}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={scrollToTop}
      />

      <div className="flex flex-col gap-1">
        {maxLength && !hideCounter && (
          <Typo
            type="bodyXS"
            textColor={isOverLimit ? "text-text-error" : "text-text-secondary"}
          >
            {counterText}
          </Typo>
        )}
        {error && <FieldError text={error} className="text-text-error" />}
      </div>
    </div>
  );
};
